import React from 'react';
import useGameStore from '../engine/store';
import { ECONOMY_STATES } from '../engine/economyEngine';

function formatMoney(value) {
  if (value === undefined || value === null || isNaN(value)) return '$0';
  if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function StatBox({ label, value, color = 'text-white', subtext, icon }) {
  return (
    <div className="glass-panel p-3">
      <div className="flex items-center gap-1.5 mb-1">
        {icon && <span className="text-xs">{icon}</span>}
        <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-lg font-black font-mono ${color}`}>{value}</div>
      {subtext && <div className="text-xs text-gray-500 mt-0.5">{subtext}</div>}
    </div>
  );
}

function RiskMeter() {
  const { riskScore } = useGameStore();

  const getColor = () => {
    if (riskScore <= 30) return '#00ff88';
    if (riskScore <= 60) return '#ffcc00';
    return '#ff3366';
  };

  const getLabel = () => {
    if (riskScore <= 20) return 'Very Safe';
    if (riskScore <= 40) return 'Conservative';
    if (riskScore <= 60) return 'Balanced';
    if (riskScore <= 80) return 'Aggressive';
    return 'DANGEROUS';
  };

  const color = getColor();
  const angle = (riskScore / 100) * 180;

  return (
    <div className="glass-panel p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wider mb-3">Portfolio Risk</div>

      <div className="relative w-full flex justify-center mb-2">
        <svg width="140" height="80" viewBox="0 0 140 80">
          <path d="M 10 75 A 60 60 0 0 1 130 75" fill="none" stroke="#243044" strokeWidth="10" strokeLinecap="round" />
          <path d="M 10 75 A 60 60 0 0 1 130 75" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
            strokeDasharray={`${(angle / 180) * 188} 188`}
            style={{ filter: `drop-shadow(0 0 8px ${color}40)`, transition: 'all 0.6s ease-out' }}
          />
          <line
            x1="70" y1="75"
            x2={70 + 48 * Math.cos(Math.PI - (angle * Math.PI / 180))}
            y2={75 - 48 * Math.sin(Math.PI - (angle * Math.PI / 180))}
            stroke="white" strokeWidth="2" strokeLinecap="round"
            style={{ transition: 'all 0.6s ease-out' }}
          />
          <circle cx="70" cy="75" r="4" fill="white" />
        </svg>
      </div>

      <div className="text-center">
        <div className="text-3xl font-black font-mono" style={{ color }}>{riskScore}</div>
        <div className="text-xs text-gray-400 mt-0.5">{getLabel()}</div>
      </div>

      <div className="flex justify-between text-xs mt-3 px-1">
        <span className="text-neon-green">0</span>
        <span className="text-neon-yellow">50</span>
        <span className="text-neon-red">100</span>
      </div>
    </div>
  );
}

function EconomyIndicator() {
  const { economyState, activeEvents } = useGameStore();

  const config = {
    [ECONOMY_STATES.BOOM]: { emoji: '📈', label: 'Boom', color: 'text-neon-green', bg: 'bg-neon-green/10' },
    [ECONOMY_STATES.NORMAL]: { emoji: '📊', label: 'Normal', color: 'text-neon-blue', bg: 'bg-neon-blue/10' },
    [ECONOMY_STATES.RECESSION]: { emoji: '📉', label: 'Recession', color: 'text-neon-red', bg: 'bg-neon-red/10' },
  };

  const c = config[economyState] || config[ECONOMY_STATES.NORMAL];

  return (
    <div className="glass-panel p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Economy</div>
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${c.bg}`}>
        <span className="text-xl">{c.emoji}</span>
        <span className={`font-bold ${c.color}`}>{c.label}</span>
      </div>

      {activeEvents.length > 0 && (
        <div className="mt-3 space-y-1.5 max-h-[180px] overflow-y-auto">
          {activeEvents.map((event, i) => (
            <div key={`${event.id}-${i}`} className="flex items-center justify-between text-xs p-1.5 rounded bg-dark-800/60">
              <span className="text-gray-400 truncate flex-1">{event.title}</span>
              <span className="text-gray-600 ml-2 flex-shrink-0">{event.remainingMonths}mo</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HealthBar() {
  const { bankHealth, liquidity, totalCapital } = useGameStore();

  const config = {
    healthy: { label: 'Healthy', color: '#00ff88', emoji: '💚' },
    warning: { label: 'Low Liquidity', color: '#ffcc00', emoji: '⚠️' },
    critical: { label: 'CRITICAL', color: '#ff3366', emoji: '🚨' },
    bankrupt: { label: 'BANKRUPT', color: '#ff0000', emoji: '💀' },
  };

  const c = config[bankHealth] || config.healthy;
  const liquidityPct = totalCapital > 0 ? Math.min(100, (liquidity / totalCapital) * 100) : 0;

  return (
    <div className="glass-panel p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Bank Health</div>
      <div className="flex items-center gap-2 mb-3">
        <span>{c.emoji}</span>
        <span className="font-bold text-sm" style={{ color: c.color }}>{c.label}</span>
      </div>
      <div>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-500">Liquidity Ratio</span>
          <span className="font-mono text-gray-300">{liquidityPct.toFixed(0)}%</span>
        </div>
        <div className="h-2 rounded-full bg-dark-600 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${liquidityPct}%`,
              background: liquidityPct > 30 ? '#00ff88' : liquidityPct > 15 ? '#ffcc00' : '#ff3366',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function BankStats() {
  const {
    totalCapital, liquidity, totalLoansOutstanding, month,
    currentMonthIncome, currentMonthLosses, activeLoans,
    totalLoansApproved, totalLoansRejected, totalProfit, reputation,
    saveGame,
  } = useGameStore();

  return (
    <div className="h-full overflow-y-auto p-3 space-y-3">
      {/* Core financials */}
      <div className="grid grid-cols-2 gap-2">
        <StatBox icon="🏦" label="Capital" value={formatMoney(totalCapital)} color="text-neon-blue" />
        <StatBox icon="💵" label="Cash" value={formatMoney(liquidity)} color="text-neon-green"
          subtext={totalCapital > 0 ? `${((liquidity / totalCapital) * 100).toFixed(0)}% liquid` : ''} />
        <StatBox icon="📋" label="Outstanding" value={formatMoney(totalLoansOutstanding)} color="text-neon-yellow" />
        <StatBox icon="📅" label="Month" value={month} color="text-white"
          subtext={`Year ${Math.ceil(month / 12)}`} />
      </div>

      {/* Monthly P&L */}
      <div className="glass-panel p-3">
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Last Month</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-xs text-gray-500">Income</div>
            <div className="text-sm font-bold font-mono text-neon-green">+{formatMoney(currentMonthIncome)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Losses</div>
            <div className="text-sm font-bold font-mono text-neon-red">-{formatMoney(currentMonthLosses)}</div>
          </div>
        </div>
      </div>

      <RiskMeter />
      <HealthBar />
      <EconomyIndicator />

      {/* Portfolio stats */}
      <div className="glass-panel p-3">
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Career Stats</div>
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Active Loans</span>
            <span className="font-mono text-white">{activeLoans.length}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Total Approved</span>
            <span className="font-mono text-neon-green">{totalLoansApproved}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Total Rejected</span>
            <span className="font-mono text-neon-red">{totalLoansRejected}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Total Profit</span>
            <span className={`font-mono ${totalProfit >= 0 ? 'text-neon-green' : 'text-neon-red'}`}>
              {totalProfit >= 0 ? '+' : ''}{formatMoney(totalProfit)}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Reputation</span>
            <span className="font-mono text-neon-purple">⭐ {reputation}</span>
          </div>
        </div>
      </div>

      {/* Save button */}
      <button
        onClick={saveGame}
        className="w-full py-2.5 rounded-lg text-xs font-semibold bg-dark-600 border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all"
      >
        💾 Save Game
      </button>
    </div>
  );
}
